import {useQuery} from "@apollo/client";
import classnames from "classnames";
import {formatISO} from "date-fns";
import startCase from "lodash/startCase";
import {Fragment, useEffect} from "react";
import {ImPrinter} from "react-icons/im";
import {useParams} from "react-router";

import logo from "@/assets/images/default.svg";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {dateOnly, linebreaksbr, shortCurrency} from "@/lib/format.tsx";
import {GQL_INVOICE_DETAILS, InvoiceDetailsResponse} from "@/lib/queries/billing.ts";

import "./InvoiceDetailsPrintable.css";

export const InvoiceDetails = () => {
  const {user, brand} = useCurrentUserContext();
  const {transactionId} = useParams();
  const {data} = useQuery<InvoiceDetailsResponse>(GQL_INVOICE_DETAILS, {variables: {id: transactionId}});
  const invoice = data?.invoiceDetails;
  useEffect(() => {
    window.onbeforeprint = function () {
      document.body.classList.add("invoiceprinter");
    };
    window.onafterprint = function () {
      document.body.classList.remove("invoiceprinter");
    };
  }, []);

  return (
    invoice && (
      <div className={classnames("row mt-2 mx-0")}>
        <div className="col-6 white-block white-block-border rounded p-4">
          <div className="printable">
            <div className="row">
              <div className="col-3">
                <img className="img-fluid" style={{maxWidth: "160px"}} src={logo} alt={brand.name} />
              </div>
              <div className="col-9 text-end">
                <p className="text-muted">
                  Invoice #:{" "}
                  {invoice.payment_instrument_type === "credit_card" ||
                  invoice.payment_instrument_type === "paypal_account"
                    ? invoice?.id
                    : invoice?.transaction_id}
                  <br />
                  Date:{" "}
                  {dateOnly(formatISO(invoice.created_at || ""), user!.current_date_format, user!.account!.timezone)}
                </p>
              </div>
            </div>
            <div className="row my-4">
              <div className="col-6">
                <p className="fw-bold lh-sm mb-2">{brand.name} LLC</p>
                <p
                  className="lh-sm text-muted mb-2"
                  dangerouslySetInnerHTML={{
                    __html: linebreaksbr(brand.address),
                  }}
                ></p>
              </div>
              <div className="col-6 text-end">
                <p className="lh-sm fw-bold mb-2 mb-1">{user?.account.name}</p>
                {user?.account.billing_address && (
                  <p className="mb-1 text-muted">
                    {user?.account.billing_address.split("\n").map((fragment: string, idx) => (
                      <Fragment key={`address_fragment_${idx}`}>
                        {idx > 0 && <br />}
                        {fragment}
                      </Fragment>
                    ))}
                  </p>
                )}
                <p className="mb-1 text-muted ">
                  {user?.account.additional_info?.split("\n").map((fragment: string, idx) => (
                    <Fragment key={`additional_info_fragment_${idx}`}>
                      {idx > 0 && <br />}
                      {fragment}
                    </Fragment>
                  ))}
                </p>
                <p className="mb-1 text-muted ">{user?.account.primary_user_email}</p>
              </div>
            </div>
            <div className="row pulse-bg-gray-300 w-100 border-bottom">
              <div className="col-12 p-2 fw-bold">Payment Method</div>
            </div>
            <div className="row w-100">
              <div className="col-6 p-2">
                {invoice.payment_instrument_type === "credit_card"
                  ? "Credit Card"
                  : invoice.payment_instrument_type === "paypal_account"
                    ? "PayPal Account"
                    : startCase(invoice.payment_instrument_type)}
              </div>
              <div className="col-6 p-2 text-end">
                {invoice.payment_instrument_type === "credit_card" ? (
                  <>
                    {invoice.credit_card_type}: xxxx-{invoice.credit_card_suffix}
                  </>
                ) : invoice.payment_instrument_type === "paypal_account" ? (
                  invoice.paypal_account
                ) : (
                  invoice.transaction_id
                )}
              </div>
            </div>
            <div className="row pulse-bg-gray-300 w-100 mt-4 border-bottom">
              <div className="col-10 p-2 fw-bold">Item</div>
              <div className="col-2 p-2 fw-bold text-end">Price</div>
            </div>
            <div className="row w-100 mb-4">
              <div className="col-10 p-2">
                {brand.name} subscription:{" "}
                {dateOnly(invoice.period_start || "", user!.current_date_format, user!.account!.timezone)} &mdash;{" "}
                {dateOnly(invoice.period_end || "", user!.current_date_format, user!.account!.timezone)}
              </div>
              <div className="col-2 p-2 text-end">{shortCurrency(invoice.amount || 0, 2)}</div>
            </div>
            <div className="row w-100 my-4">
              <div className="col-9 p-2"></div>
              <div className="col-3 p-2 text-end border-top">
                <strong>Total: {shortCurrency(invoice.amount || 0, 2)}</strong>
              </div>
            </div>

            <div className="text-center mt-4 d-p-none">
              <button type="button" className="btn btn-link text-decoration-none" onClick={() => window.print()}>
                <ImPrinter className="me-2" />
                <span className="text-black">Print Invoice</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  );
};
